import React, { useMemo } from 'react';
import {
  Box,
  LinearProgress,
  Typography,
  IconButton,
  Tooltip,
  Avatar,
  styled,
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import UploadFileOutlinedIcon from '@mui/icons-material/UploadFileOutlined';
import VideoFileTwoToneIcon from '@mui/icons-material/VideoFileTwoTone';
import AudioFileTwoToneIcon from '@mui/icons-material/AudioFileTwoTone';
import FilePresentTwoToneIcon from '@mui/icons-material/FilePresentTwoTone';
import type { SvgIconProps } from '@mui/material';
import type { UppyFile } from '@uppy/core';
import filesize from 'filesize';
import type { MediaType } from '../editor/fields/media-field-type';

type Meta = Record<string, any>;
type Body = Record<string, any>;

// RTK Query error types (from @reduxjs/toolkit/query)
type FetchBaseQueryError =
  | {
    status: number;
    data: unknown;
  }
  | {
    status: 'FETCH_ERROR';
    data?: unknown;
    error: string;
  }
  | {
    status: 'PARSING_ERROR';
    originalStatus: number;
    data: string;
    error: string;
  }
  | {
    status: 'TIMEOUT_ERROR';
    data?: unknown;
    error: string;
  }
  | {
    status: 'CUSTOM_ERROR';
    data?: unknown;
    error: string;
  };

type SerializedError = {
  name?: string;
  message?: string;
  stack?: string;
  code?: string;
};

type RTKQueryError = FetchBaseQueryError | SerializedError;

interface UniversalMediaUploaderStatusProps {
  file?: UppyFile<Meta, Body>;
  progress?: number;
  error?: Error | RTKQueryError;
  isCompleted?: boolean;
  wasRecentlyUploaded?: boolean;
  onClose?: () => void;
  onClick?: () => void;
  closeTooltipText?: string;
  closeDisabled?: boolean;
  /** Filename for persisted items (fallback when `file` is absent) */
  name?: string;
  /** File size in bytes for persisted items (fallback when `file` is absent) */
  size?: number;
  /** Thumbnail URL for persisted items (fallback when `file` is absent) */
  thumbnailURL?: string;
  /** Controls Avatar size — 'default' = theme.spacing(5) (40px), 'large' = theme.spacing(10) (80px) */
  avatarSize?: 'default' | 'large';
  /** Media field type — used to infer the correct icon when `file` is absent */
  fileType?: MediaType;
}

const IconContainer = styled(Box)(({ theme }) => ({
  marginRight: theme.spacing(2),
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
}));

const ContentContainer = styled(Box)(({ theme }) => ({
  flex: 1,
  minWidth: 0,
  overflow: 'hidden',
  marginRight: theme.spacing(2),
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
}));

const ProgressContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  width: '95%',
  gap: theme.spacing(1),
}));

const FilenameTypography = styled(Typography)({
  display: 'flex',
  overflow: 'hidden',
  whiteSpace: 'nowrap',
});

const StatusContainer = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2),
  display: 'flex',
  alignItems: 'center',
  backgroundColor: theme.palette.background.paper,
  borderRadius: '4px',
  minHeight: '64px',
  overflow: 'hidden',
}));

export const SIZE_MAP = { default: 5, large: 10 } as const;

/** @deprecated Use MediaType from '../../editor/fields/media-field-type' */
export type { MediaType as MediaFieldType } from '../editor/fields/media-field-type';

const IMAGE_FIELD_TYPES = new Set<MediaType>(['photo', 'signature', 'sketch']);

export function isImageType(mimeType?: string, fileType?: MediaType): boolean {
  if (fileType != null) return IMAGE_FIELD_TYPES.has(fileType);
  return !!mimeType && mimeType.startsWith('image/');
}

export function getFileTypeIcon(mimeType?: string, fileType?: MediaType): React.ComponentType<SvgIconProps> {
  if (fileType === 'video' || mimeType?.startsWith('video/')) return VideoFileTwoToneIcon;
  if (fileType === 'audio' || mimeType?.startsWith('audio/')) return AudioFileTwoToneIcon;
  if (isImageType(mimeType, fileType)) return UploadFileOutlinedIcon;
  return FilePresentTwoToneIcon;
}

// ---------------------------------------------------------------------------
// Sub-components extracted to reduce cognitive complexity of the main component
// ---------------------------------------------------------------------------

interface StatusLayoutProps {
  file?: UppyFile<Meta, Body>;
  normalizedError: Error | null;
  isClickable: boolean;
  onClick?: () => void;
  onClose?: () => void;
  closeTooltipText: string;
  closeDisabled: boolean;
  displayName?: string;
  displayPreview?: string;
  thumbnailURL?: string;
  avatarSize: 'default' | 'large';
  fileType?: MediaType;
  children: React.ReactNode;
}

const StatusLayout: React.FC<StatusLayoutProps> = ({
  file,
  normalizedError,
  isClickable,
  onClick,
  onClose,
  closeTooltipText,
  closeDisabled,
  displayName,
  displayPreview,
  thumbnailURL,
  avatarSize,
  fileType,
  children,
}) => {
  const interactiveProps = isClickable
    ? {
      onClick,
      onKeyDown: (e: React.KeyboardEvent) => {
        if (e.currentTarget !== e.target) return;
        if (e.repeat) return;
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onClick?.();
        }
      },
      sx: {
        cursor: 'pointer' as const,
        '&:hover': { bgcolor: 'action.hover' },
        '&:focus-visible': { outline: '2px solid', outlineColor: 'primary.main', outlineOffset: -2 },
      },
      role: 'button' as const,
      tabIndex: 0,
      'aria-label': displayName,
    }
    : {};

  const FileTypeIcon = getFileTypeIcon(file?.type, fileType);
  const showThumbnail = isImageType(file?.type, fileType) || (!file && !!thumbnailURL);
  const avatarSrc = showThumbnail ? displayPreview : undefined;

  return (
    <StatusContainer {...interactiveProps}>
      <IconContainer>
        <Avatar
          variant="rounded"
          src={avatarSrc}
          alt={displayName}
          sx={(theme) => ({
            width: theme.spacing(SIZE_MAP[avatarSize]),
            height: theme.spacing(SIZE_MAP[avatarSize]),
            bgcolor: 'gray.light',
            '& img': { objectFit: 'cover' },
          })}
        >
          <FileTypeIcon color={normalizedError ? 'error' : 'primary'} />
        </Avatar>
      </IconContainer>

      <ContentContainer>
        {children}
      </ContentContainer>

      {(onClose || closeDisabled) && (
        <Tooltip title={closeDisabled ? '' : closeTooltipText} placement="bottom">
          <span>
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                onClose?.();
              }}
              disabled={closeDisabled}
              aria-label={closeTooltipText}
              sx={{ marginLeft: '8px', flexShrink: 0 }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      )}
    </StatusContainer>
  );
};

interface StatusContentProps {
  normalizedError: Error | null;
  isActuallyCompleted: boolean;
  isPersisted: boolean;
  filenameProps: { variant: 'body2'; color: 'textPrimary'; title?: string };
  filenameContent: React.ReactNode;
  fileSize?: number | null;
  wasRecentlyUploaded: boolean;
  progress: number;
}

function formatFileSize(fileSize: number | null | undefined, suffix = ''): string {
  if (fileSize == null) return '';
  return suffix ? `${filesize(fileSize)}${suffix}` : filesize(fileSize) as string;
}

const StatusContent: React.FC<StatusContentProps> = ({
  normalizedError,
  isActuallyCompleted,
  isPersisted,
  filenameProps,
  filenameContent,
  fileSize,
  wasRecentlyUploaded,
  progress,
}) => {
  if (normalizedError) {
    return (
      <>
        <FilenameTypography {...filenameProps} color="error">
          {filenameContent}
        </FilenameTypography>
        <Typography variant="body2" color="error" sx={{ fontSize: '0.75rem' }}>
          {formatFileSize(fileSize, ' • ')}
          {`Upload failed: ${normalizedError.message}`}
        </Typography>
      </>
    );
  }

  if (isActuallyCompleted) {
    return (
      <>
        <FilenameTypography {...filenameProps}>
          {filenameContent}
        </FilenameTypography>
        <Typography variant="body2" color="textSecondary" sx={{ fontSize: '0.75rem' }}>
          {formatFileSize(fileSize)}
          {wasRecentlyUploaded ? ' • Complete' : ''}
        </Typography>
      </>
    );
  }

  if (isPersisted) {
    return (
      <>
        <FilenameTypography {...filenameProps}>
          {filenameContent}
        </FilenameTypography>
        {fileSize != null && (
          <Typography variant="body2" color="textSecondary" sx={{ fontSize: '0.75rem' }}>
            {filesize(fileSize)}
          </Typography>
        )}
      </>
    );
  }

  return (
    <>
      <FilenameTypography {...filenameProps}>
        {filenameContent}
      </FilenameTypography>
      <Typography variant="body2" color="textSecondary" sx={{ fontSize: '0.75rem' }}>
        {formatFileSize(fileSize, ' • ')}
        {`Loading ${Math.round(progress)}%`}
      </Typography>
      <ProgressContainer>
        <LinearProgress
          variant="determinate"
          value={progress}
          color="secondary"
          sx={{ flex: 1, height: '4px' }}
        />
      </ProgressContainer>
    </>
  );
};

export function splitFilename(filename: string): { head: string; tail: string } {
  const dotIndex = filename.lastIndexOf('.');
  const hasExtension = dotIndex > 0;
  const extension = hasExtension ? filename.slice(dotIndex) : '';
  const stem = hasExtension ? filename.slice(0, dotIndex) : filename;

  const tailLength = 4;
  const minHeadLength = 10;
  if (stem.length <= tailLength + minHeadLength) {
    return { head: filename, tail: '' };
  }

  const head = stem.slice(0, stem.length - tailLength);
  const tail = stem.slice(-tailLength) + extension;
  return { head, tail };
}

function normalizeError(error?: Error | RTKQueryError): Error | null {
  if (!error) return null;

  if (error instanceof Error) return error;

  if ('message' in error && error.message) {
    return new Error(error.message);
  }

  if ('status' in error) {
    if (typeof error.status === 'string') {
      const errorMessage = ('error' in error && typeof error.error === 'string')
        ? error.error
        : `Request failed: ${error.status}`;
      return new Error(errorMessage);
    }

    if (typeof error.status === 'number') {
      return new Error(`Request failed with status ${error.status}`);
    }
  }

  return new Error('An unknown error occurred');
}

const UniversalMediaUploaderStatus: React.FC<UniversalMediaUploaderStatusProps> = ({
  file,
  progress = 0,
  error,
  isCompleted = false,
  wasRecentlyUploaded = false,
  onClose,
  onClick,
  closeTooltipText = 'Remove',
  closeDisabled = false,
  name: directName,
  size: directSize,
  thumbnailURL,
  avatarSize = 'default',
  fileType,
}) => {
  // Resolve display values — Uppy file takes precedence over direct props
  const displayName = file?.name ?? directName;
  const displaySize = file?.size ?? directSize;
  const displayPreview = file?.preview ?? thumbnailURL;

  const normalizedError = useMemo(() => normalizeError(error), [error]);

  if (!file && !normalizedError && !displayName && !displaySize && !displayPreview) {
    return null;
  }

  const isActuallyCompleted = isCompleted || progress >= 100;

  const { head: filenameHead, tail: filenameTail } = displayName
    ? splitFilename(displayName)
    : { head: '', tail: '' };
  // Persisted item with no active upload - no progress bar,
  // no "Complete" badge, no "Loading" text. Just filename and file size.
  const isPersisted = !file && !normalizedError && !isCompleted && progress === 0;

  const filenameProps = {
    variant: 'body2' as const,
    color: 'textPrimary' as const,
    title: displayName,
  };

  const filenameContent = (
    <>
      <Box
        component="span"
        sx={{
          flex: '0 1 auto', minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis',
        }}
      >
        {filenameHead}
      </Box>
      {filenameTail && (
        <Box component="span" sx={{ flexShrink: 0 }}>
          {filenameTail}
        </Box>
      )}
    </>
  );

  const isClickable = (isActuallyCompleted || isPersisted) && !normalizedError && !!onClick;

  return (
    <StatusLayout
      file={file}
      normalizedError={normalizedError}
      isClickable={isClickable}
      onClick={onClick}
      onClose={onClose}
      closeTooltipText={closeTooltipText}
      closeDisabled={closeDisabled}
      displayName={displayName}
      displayPreview={displayPreview}
      thumbnailURL={thumbnailURL}
      avatarSize={avatarSize}
      fileType={fileType}
    >
      <StatusContent
        normalizedError={normalizedError}
        isActuallyCompleted={isActuallyCompleted}
        isPersisted={isPersisted}
        filenameProps={filenameProps}
        filenameContent={filenameContent}
        fileSize={displaySize}
        wasRecentlyUploaded={wasRecentlyUploaded}
        progress={progress}
      />
    </StatusLayout>
  );
};

export default UniversalMediaUploaderStatus;
